import type {SurveyEvaluationRequestDTO}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {createContext, useContext, useEffect, useMemo, useRef} from "react";
import {useQuery} from "react-query";
import {DateRange} from "../../common/newDatePicker/datePickerShared";
import {SurveyEvaluationFilterState} from "./SurveyEvaluationFilters";
import {SURVEY_EVALUATION_QUERY_KEY} from "./surveyQueryRefresh";

/** As the date control writes a range: "08.09.2026 – 07.10.2026". */
const formatRange = ({start, end}: DateRange) => `${formatFullDMY(start)} – ${formatFullDMY(end)}`

export const exclusiveEnd = (inclusiveEndOfRange: Date) => {
    const end = new Date(inclusiveEndOfRange)
    end.setDate(end.getDate() + 1)
    return end
}

export const buildSurveyEvaluationRequest = (
    surveyId: string,
    filters: SurveyEvaluationFilterState,
    includeSensitive?: boolean
): SurveyEvaluationRequestDTO => ({
    surveyId,
    includeSensitive: includeSensitive || undefined,
    from: filters.range.start,
    to: exclusiveEnd(filters.range.end),
    compareFrom: filters.compareRange?.start,
    compareTo: filters.compareRange ? exclusiveEnd(filters.compareRange.end) : undefined,
    languages: filters.languages.length ? filters.languages : undefined,
    sectorIds: filters.sectorIds.length ? filters.sectorIds : undefined,
    reviewStates: filters.reviewStates.length ? filters.reviewStates : undefined,
    guestCategoryId: filters.guestCategoryId ?? undefined,
    surveyVersions: filters.versions.length ? filters.versions : undefined,
    minSubmissionCount: filters.minSubmissions ?? undefined,
})

export const useSurveyEvaluation = (surveyId: string | undefined, filters: SurveyEvaluationFilterState) => {
    const {t} = useLocalize()

    const query = useQuery(
        [SURVEY_EVALUATION_QUERY_KEY, surveyId, filters],
        () => SurveyAggregationService.getEvaluation(buildSurveyEvaluationRequest(surveyId!, filters)),
        {enabled: !!surveyId, refetchOnWindowFocus: false, keepPreviousData: true}
    )

    /*
        A new period keeps the old numbers on screen until the new ones land. Another survey must
        not: its questions have their own ids, so the previous survey's answers would match none of
        them and every card would read 0 for a moment.
     */
    const loadedSurveyId = useRef<string | undefined>()

    useEffect(() => {
        if (query.isSuccess && !query.isPreviousData) loadedSurveyId.current = surveyId
    }, [query.isSuccess, query.isPreviousData, surveyId])

    const surveyChanged = query.isPreviousData && loadedSurveyId.current !== surveyId
    const evaluation = surveyChanged ? undefined : query.data
    const loading = query.isLoading || surveyChanged

    // Each period by its dates, as the date control names them: the compare range may be a year back, not the weeks before.
    const currentCaption = formatRange(filters.range)
    const compareCaption = filters.compareRange ? formatRange(filters.compareRange) : t('Previous value')

    const scope = useMemo<SurveyEvaluationScope>(() => ({surveyId, filters}), [surveyId, filters])

    return {evaluation, loading, currentCaption, compareCaption, scope}
}

/**
 * What a question card needs to ask for the same evaluation again. The cards sit several levels
 * below the tab and are handed one question's fold, not the range they were folded over.
 */
export interface SurveyEvaluationScope {
    surveyId: string | undefined
    filters: SurveyEvaluationFilterState
}

export const SurveyEvaluationScopeContext = createContext<SurveyEvaluationScope | null>(null)

/**
 * The withheld evaluation and the revealed one are two answers to two different questions, so they
 * get a query key each: hiding the sensitive answers again shows the cached withheld copy at once,
 * and no revealed text is left lying in the cache under the key the page reads by default.
 */
export const useRevealedSurveyEvaluation = (enabled: boolean) => {
    const scope = useContext(SurveyEvaluationScopeContext)
    const surveyId = scope?.surveyId

    const query = useQuery(
        [SURVEY_EVALUATION_QUERY_KEY, surveyId, scope?.filters, 'sensitive'],
        () => SurveyAggregationService.getEvaluation(buildSurveyEvaluationRequest(surveyId!, scope!.filters, true)),
        {enabled: enabled && !!surveyId, refetchOnWindowFocus: false}
    )

    return {evaluation: query.data, available: scope != null, loading: query.isFetching && query.data == null}
}

export const useSurveySectorOptions = (survey: Survey | undefined) => {
    const allSectors = useAllSectors()

    return useMemo(() => (
        survey?.sectorIds?.length
            ? allSectors.filter(sector => sector.id && survey.sectorIds!.includes(sector.id))
            : allSectors
    ), [allSectors, survey?.sectorIds])
}
